import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentReviewStatus } from '../../../../mocks/cases';
import CategoryHeader from './CategoryHeader';
import type { DocState } from './types';
import { randomSize, reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

interface Props {
  docs: DocState[];
  onDocsChange: (next: DocState[]) => void;
  oarCaseNumber: string;
  onOarCaseNumberChange: (value: string) => void;
  categoryReview: DocumentReviewStatus;
  onCategoryReviewChange: (status: DocumentReviewStatus) => void;
  onViewDocument?: (doc: DocState) => void;
}

export default function AsylumCategory({ docs, onDocsChange, oarCaseNumber, onOarCaseNumberChange, categoryReview, onCategoryReviewChange, onViewDocument }: Props) {
  const { t } = useTranslation();
  const cardFileRef = useRef<HTMLInputElement>(null);
  const receiptFileRef = useRef<HTMLInputElement>(null);
  const resolutionFileRef = useRef<HTMLInputElement>(null);
  const otherFileRef = useRef<HTMLInputElement>(null);

  const cardDocs    = docs.filter((d) => d.category === 'asylum' && d.docType === 'card');
  const receiptDocs = docs.filter((d) => d.category === 'asylum' && d.docType === 'receipt');
  const resolutionDocs = docs.filter((d) => d.category === 'asylum' && d.docType === 'resolution');
  const otherDocs = docs.filter(
    (d) => d.category === 'asylum' && !['card', 'receipt', 'resolution'].includes(String(d.docType || '').toLowerCase()),
  );

  /* ── Shared upload-buttons row ── */
  const UploadButtons = ({ onChoose }: { onChoose: () => void }) => (
    <div className="grid grid-cols-1 gap-2">
      <button
        type="button"
        onClick={onChoose}
        className="min-h-[40px] flex items-center justify-center gap-1.5 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer"
      >
        <i className="ri-file-upload-line text-sm" /> {t('docUpload.chooseFile', { defaultValue: 'Choose file' })}
      </button>
    </div>
  );

  /* ── Uploaded doc row ── */
  const UploadedRow = ({
    doc,
    onChangeClick,
    onRemove,
    onView,
  }: {
    doc: DocState;
    onChangeClick: () => void;
    onRemove: () => void;
    onView: () => void;
  }) => (
    <div className="bg-emerald-50 rounded-lg px-3 py-2 flex flex-col gap-2 mb-2 min-[420px]:flex-row min-[420px]:items-center">
      <i className="ri-file-check-line text-emerald-500 text-base shrink-0" />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="min-w-0 text-[13px] font-medium text-slate-700 truncate">
          {doc.uploadedFile ?? doc.name}
        </span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(doc.reviewStatus)}`}>
          {reviewStatusLabel(doc.reviewStatus)}
        </span>
      </div>
      <button
        type="button"
        onClick={onView}
        className="min-h-[36px] text-[12px] text-slate-600 hover:text-slate-700 cursor-pointer whitespace-nowrap shrink-0 border border-slate-200 rounded-md px-2 py-1 bg-white hover:bg-slate-50"
      >
        View
      </button>
      <button
        type="button"
        onClick={onChangeClick}
        className="min-h-[36px] text-[12px] text-slate-500 hover:text-slate-700 cursor-pointer whitespace-nowrap shrink-0"
      >
        Change
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-rose-500 cursor-pointer shrink-0"
      >
        <i className="ri-delete-bin-line text-sm" />
      </button>
    </div>
  );

  return (
    <div className="max-w-[720px]">
      <CategoryHeader title={t('advisorPortal.historyDocAsylumDocuments')} category="asylum" status={categoryReview} onChange={onCategoryReviewChange} />

      {/* OAR case number */}
      <div className="mb-5 max-w-[480px]">
        <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">{t('advisorPortal.oarCaseNumber', { defaultValue: 'OAR case number' })}</label>
        <input
          type="text"
          value={oarCaseNumber}
          onChange={(e) => onOarCaseNumberChange(e.target.value)}
          placeholder="OAR/2024/12345"
          className={`w-full min-h-[44px] px-3 bg-white border rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
            oarCaseNumber ? 'border-emerald-400' : 'border-slate-200'
          }`}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-3 max-w-[480px]">
        <div className="mb-3">
          <p className="text-[14px] font-semibold text-slate-800">Asylum receipt (resguardo)</p>
        </div>

        {receiptDocs.map((doc) => (
          <UploadedRow
            key={doc.id}
            doc={doc}
            onView={() => onViewDocument?.(doc)}
            onChangeClick={() => {
              onDocsChange(docs.filter((d) => d.id !== doc.id));
              setTimeout(() => receiptFileRef.current?.click(), 50);
            }}
            onRemove={() => onDocsChange(docs.filter((d) => d.id !== doc.id))}
          />
        ))}

        <UploadButtons onChoose={() => receiptFileRef.current?.click()} />

        <input
          ref={receiptFileRef}
          type="file"
          accept={DOCUMENT_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onDocsChange([...docs, {
              id: `asy-${Date.now()}`,
              name: 'Asylum receipt',
              category: 'asylum',
              docType: 'receipt',
              uploaded: true,
              uploadedAt: new Date().toISOString().slice(0, 10),
              uploadedFile: file.name,
              rawFile: file,
              reviewStatus: 'pending' as DocumentReviewStatus,
              fileSize: randomSize(),
            }]);
            e.target.value = '';
          }}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-3 max-w-[480px]">
        <div className="mb-3">
          <p className="text-[14px] font-semibold text-slate-800">Asylum applicant card</p>
        </div>

        {cardDocs.map((doc) => (
          <UploadedRow
            key={doc.id}
            doc={doc}
            onView={() => onViewDocument?.(doc)}
            onChangeClick={() => {
              onDocsChange(docs.filter((d) => d.id !== doc.id));
              setTimeout(() => cardFileRef.current?.click(), 50);
            }}
            onRemove={() => onDocsChange(docs.filter((d) => d.id !== doc.id))}
          />
        ))}

        <UploadButtons onChoose={() => cardFileRef.current?.click()} />

        <input
          ref={cardFileRef}
          type="file"
          accept={DOCUMENT_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onDocsChange([...docs, {
              id: `asy-${Date.now()}`,
              name: 'Asylum card',
              category: 'asylum',
              docType: 'card',
              uploaded: true,
              uploadedAt: new Date().toISOString().slice(0, 10),
              uploadedFile: file.name,
              rawFile: file,
              reviewStatus: 'pending' as DocumentReviewStatus,
              fileSize: randomSize(),
            }]);
            e.target.value = '';
          }}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-3 max-w-[480px]">
        <div className="mb-3">
          <p className="text-[14px] font-semibold text-slate-800">Resolution or admission notice</p>
        </div>
        {resolutionDocs.map((doc) => (
          <UploadedRow
            key={doc.id}
            doc={doc}
            onView={() => onViewDocument?.(doc)}
            onChangeClick={() => {
              onDocsChange(docs.filter((d) => d.id !== doc.id));
              setTimeout(() => resolutionFileRef.current?.click(), 50);
            }}
            onRemove={() => onDocsChange(docs.filter((d) => d.id !== doc.id))}
          />
        ))}
        <UploadButtons onChoose={() => resolutionFileRef.current?.click()} />
        <input
          ref={resolutionFileRef}
          type="file"
          accept={DOCUMENT_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onDocsChange([...docs, {
              id: `asy-${Date.now()}`,
              name: 'Asylum resolution',
              category: 'asylum',
              docType: 'resolution',
              uploaded: true,
              uploadedAt: new Date().toISOString().slice(0, 10),
              uploadedFile: file.name,
              rawFile: file,
              reviewStatus: 'pending' as DocumentReviewStatus,
              fileSize: randomSize(),
            }]);
            e.target.value = '';
          }}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 max-w-[480px]">
        <div className="mb-3">
          <p className="text-[14px] font-semibold text-slate-800">{t('advisorPortal.otherAsylumDocuments', { defaultValue: 'Other asylum documents' })}</p>
        </div>
        {otherDocs.map((doc) => (
          <UploadedRow
            key={doc.id}
            doc={doc}
            onView={() => onViewDocument?.(doc)}
            onChangeClick={() => {
              onDocsChange(docs.filter((d) => d.id !== doc.id));
              setTimeout(() => otherFileRef.current?.click(), 50);
            }}
            onRemove={() => onDocsChange(docs.filter((d) => d.id !== doc.id))}
          />
        ))}
        <UploadButtons onChoose={() => otherFileRef.current?.click()} />
        <input
          ref={otherFileRef}
          type="file"
          accept={DOCUMENT_FILE_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onDocsChange([...docs, {
              id: `asy-${Date.now()}`,
              name: 'Other asylum document',
              category: 'asylum',
              docType: 'other',
              uploaded: true,
              uploadedAt: new Date().toISOString().slice(0, 10),
              uploadedFile: file.name,
              rawFile: file,
              reviewStatus: 'pending' as DocumentReviewStatus,
              fileSize: randomSize(),
            }]);
            e.target.value = '';
          }}
        />
      </div>

    </div>
  );
}
